import { useEffect, useState, type ReactElement, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { accountingApi, receiptApi, ApiError, type Account, type Asset, type JournalEntry, type AfaRunPreview, type Susa, type YearClosingChecklist, type AccountMapping, type ClosingArtifact, type OrphanEntry, type ImportPreview, type ChecklistRef } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast } from '../ToastProvider';
import { Button, Card, EmptyState, ErrorNote, Field, PageHeader, euro } from '../ui';

type Tab = 'journal' | 'susa' | 'accounts' | 'assets' | 'closing' | 'dataimport' | 'import';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export default function Accounting(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const [tab, setTab] = useState<Tab>('journal');

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Buchhaltung" subtitle="Buchungen, Kontenrahmen und Anlagen (AfA)." />

      <div className="mb-6 flex gap-2 border-b border-gray-200">
        {([
          ['journal', 'Buchungen'],
          ['susa', 'Summen & Salden'],
          ['assets', 'Anlagen (AfA)'],
          ['closing', 'Jahresabschluss'],
          ['accounts', 'Konten'],
          ['dataimport', 'Daten importieren'],
          ['import', 'Import prüfen'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {active && tab === 'journal' && <JournalTab companyId={active.id} confirm={confirm} />}
      {active && tab === 'susa' && <SusaTab companyId={active.id} />}
      {active && tab === 'closing' && <ClosingTab companyId={active.id} confirm={confirm} onNavigate={setTab} />}
      {active && tab === 'accounts' && <AccountsTab companyId={active.id} />}
      {active && tab === 'dataimport' && <DataImportTab companyId={active.id} confirm={confirm} />}
      {active && tab === 'import' && <ImportFixTab companyId={active.id} confirm={confirm} />}
      {active && tab === 'assets' && <AssetsTab companyId={active.id} confirm={confirm} />}
    </div>
  );
}

// ---- Konten ----
function AccountsTab({ companyId }: { companyId: string }): ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  useEffect(() => {
    accountingApi.accounts(companyId).then(setAccounts).catch(() => setAccounts([]));
  }, [companyId]);

  const typeLabel: Record<string, string> = {
    asset: 'Aktiva', liability: 'Passiva', equity: 'Eigenkapital', revenue: 'Erlöse', expense: 'Aufwand',
  };
  return (
    <Card>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-gray-500">
            <th className="py-2">Nr.</th><th className="py-2">Konto</th><th className="py-2">Art</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.id} className="border-b border-gray-50">
              <td className="py-2 font-mono">{a.number}</td>
              <td className="py-2">{a.name}</td>
              <td className="py-2 text-gray-500">{typeLabel[a.type] ?? a.type}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---- Import prüfen: fehlende Konten per KI zuordnen ----
const ACTION_LABEL: Record<string, string> = {
  create: 'Konto anlegen', map: 'Umleiten', closing: 'Abschlussbuchung', skip: 'Ignorieren',
};
const ACTION_COLOR: Record<string, string> = {
  create: 'text-emerald-600 dark:text-emerald-400', map: 'text-blue-600 dark:text-blue-400',
  closing: 'text-amber-600 dark:text-amber-400', skip: 'text-gray-400',
};

// ---- Daten importieren (Lexware, DATEV, ...) ----
function DataImportTab({ companyId, confirm }: { companyId: string; confirm: ReturnType<typeof useConfirm> }): ReactElement {
  const toast = useToast();
  const [fileData, setFileData] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const FORMAT_LABEL: Record<string, string> = { lexware: 'Lexware-Journal (XML)', datev: 'DATEV-Buchungsstapel (CSV)', unknown: 'Unbekannt' };

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setPreview(null);
    setFileName(file.name);
    const buf = await file.arrayBuffer();
    // -> base64 (ohne data:-Prefix)
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    const b64 = btoa(binary);
    setFileData(b64);
    // direkt analysieren
    setAnalyzing(true);
    try {
      const d = await accountingApi.importPreview(companyId, b64, file.name);
      setPreview(d.preview);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Datei konnte nicht analysiert werden.');
    } finally { setAnalyzing(false); }
  }

  async function doImport() {
    if (!fileData || !preview) return;
    const c = preview.counts;
    const ok = await confirm({
      title: 'Import durchführen?',
      message: `${c.expenses} Belege, ${c.incomes} Rechnungen, ${c.assets} Anlagen und ${c.journals} Buchungen werden angelegt${preview.newAccounts.length ? `, ${preview.newAccounts.length} fehlende Konten neu erstellt` : ''}. Stornos werden verrechnet, Abschreibungen rechnet Saldify selbst. Erneuter Import derselben Datei erzeugt keine Dubletten.`,
      confirmLabel: 'Jetzt importieren',
    });
    if (!ok) return;
    setImporting(true);
    try {
      const { result } = await accountingApi.importApply(companyId, fileData, fileName);
      toast.success(`Import fertig: ${result.createdReceipts} Belege · ${result.createdInvoices} Rechnungen · ${result.createdAssets} Anlagen · ${result.createdJournalEntries} Buchungen${result.alreadyImported ? ` · ${result.alreadyImported} bereits vorhanden` : ''}.`);
      setPreview(null); setFileData(null); setFileName('');
    } catch (e) { toast.error(e instanceof ApiError ? e.message : 'Import fehlgeschlagen.'); }
    finally { setImporting(false); }
  }

  const c = preview?.counts;

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-1 text-sm font-semibold text-gray-900">Buchhaltung importieren</h3>
        <p className="mb-3 text-sm text-gray-600">
          Lade einen Journal-Export aus deinem bisherigen Programm hoch. Unterstützt: <strong>Lexware</strong> (XML-Journal)
          und <strong>DATEV</strong> (Buchungsstapel-CSV). Aufwand wird als Beleg, Erlöse als Rechnung und Anlagenkäufe als
          Anlagegut (mit AfA) angelegt. Stornos und Abschreibungen werden korrekt behandelt – keine Doppelzählung.
        </p>
        <label className="inline-block cursor-pointer rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Datei auswählen
          <input type="file" accept=".xml,.csv,.txt,text/xml,text/csv" className="hidden" onChange={onFile} />
        </label>
        {fileName && <span className="ml-3 text-sm text-gray-500">{fileName}</span>}
        {analyzing && <p className="mt-3 text-sm text-gray-500">Datei wird analysiert …</p>}
        {error && <div className="mt-3"><ErrorNote error={error} /></div>}
      </Card>

      {preview && c && (
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Vorschau</h3>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {FORMAT_LABEL[preview.format] ?? preview.format} · {preview.totalRows} Zeilen
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Belege (Aufwand)" value={c.expenses} />
            <Stat label="Rechnungen (Erlös)" value={c.incomes} />
            <Stat label="Anlagen (AfA/GWG)" value={c.assets} />
            <Stat label="Buchungen" value={c.journals} />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {c.stornosNetted > 0 && <span className="mr-3">{c.stornosNetted} Storno-Paar(e) verrechnet</span>}
            {c.skipped > 0 && <span>{c.skipped} Zeile(n) übersprungen (z. B. Abschreibungen – rechnet Saldify selbst)</span>}
          </div>

          {preview.euerByYear.length > 0 && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
              <p className="mb-2 text-xs font-semibold text-blue-900">EÜR-Vorschau (so würde das Jahr aussehen — zum Abgleich mit deinem alten Programm)</p>
              <div className="space-y-3">
                {preview.euerByYear.map((y) => (
                  <div key={y.year} className="rounded-md border border-gray-200 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Jahr {y.year}</span>
                      <span className="text-sm font-semibold text-gray-900">Überschuss {euro(y.ueberschussCents)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Einnahmen</span><span className="font-medium">{euro(y.einnahmenCents)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>Ausgaben</span><span className="font-medium">{euro(y.ausgabenCents)}</span>
                    </div>
                    {y.positionen.length > 0 && (
                      <ul className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
                        {y.positionen.map((p, i) => (
                          <li key={i} className="flex justify-between gap-2">
                            <span className="truncate">{p.label}</span>
                            <span className="shrink-0">{euro(p.cents)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-blue-700">
                Hinweis: Bei linearer AfA zeigt die Vorschau nur das Anschaffungsjahr; die Abschreibung der Folgejahre entsteht beim Import zusätzlich.
              </p>
            </div>
          )}

          {preview.warnings.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-700">
              {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}

          {preview.newAccounts.length > 0 && (
            <div className="mt-4">
              <p className="mb-1 text-xs font-semibold text-gray-700">{preview.newAccounts.length} fehlende Konten werden angelegt:</p>
              <div className="max-h-40 overflow-y-auto rounded border border-gray-200">
                <table className="w-full text-left text-xs">
                  <tbody>
                    {preview.newAccounts.map((a) => (
                      <tr key={a.number} className="border-b border-gray-100 last:border-0">
                        <td className="px-2 py-1 font-mono text-gray-700">{a.number}</td>
                        <td className="px-2 py-1 text-gray-700">{a.name}</td>
                        <td className="px-2 py-1 text-gray-400">{a.type}</td>
                        <td className="px-2 py-1 text-right">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] ${a.source === 'skr' ? 'bg-green-100 text-green-700' : a.source === 'ai' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {a.source === 'skr' ? 'SKR-Standard' : a.source === 'ai' ? 'KI' : 'vorläufig'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(preview.sampleExpenses.length > 0 || preview.sampleIncomes.length > 0) && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {preview.sampleExpenses.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-700">Beispiel-Belege</p>
                  <ul className="space-y-0.5 text-xs text-gray-600">
                    {preview.sampleExpenses.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{s.date} · {s.text} <span className="text-gray-400">({s.account})</span></span>
                        <span className="shrink-0 font-medium">{euro(s.grossCents)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {preview.sampleIncomes.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-700">Beispiel-Rechnungen</p>
                  <ul className="space-y-0.5 text-xs text-gray-600">
                    {preview.sampleIncomes.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{s.date} · {s.text} <span className="text-gray-400">({s.account})</span></span>
                        <span className="shrink-0 font-medium">{euro(s.grossCents)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <Button onClick={doImport} disabled={importing}>{importing ? 'Importiere …' : 'Jetzt importieren'}</Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
      <div className="text-lg font-semibold text-gray-900">{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
    </div>
  );
}

function ImportFixTab({ companyId, confirm }: { companyId: string; confirm: ReturnType<typeof useConfirm> }): ReactElement {
  const toast = useToast();
  const [rows, setRows] = useState<AccountMapping[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [artifacts, setArtifacts] = useState<ClosingArtifact[]>([]);
  const [resolving, setResolving] = useState(false);
  const [orphans, setOrphans] = useState<OrphanEntry[]>([]);
  const [orphanBusy, setOrphanBusy] = useState(false);

  function loadArtifacts() {
    accountingApi.closingArtifacts(companyId).then((d) => setArtifacts(d.artifacts)).catch(() => setArtifacts([]));
    accountingApi.orphanEntries(companyId).then((d) => setOrphans(d.orphans)).catch(() => setOrphans([]));
  }
  useEffect(() => { loadArtifacts(); /* eslint-disable-next-line */ }, [companyId]);

  async function resolveOrphans() {
    const ok = await confirm({
      title: 'Verwaiste Buchungen bereinigen?',
      message: `${orphans.length} Buchung(en) gehören zu keinem Beleg mehr (z. B. doppelt erfasste Einnahmen). Sie werden GoBD-konform storniert. Das korrigiert SuSa und EÜR.`,
      confirmLabel: 'Stornieren',
    });
    if (!ok) return;
    setOrphanBusy(true);
    try {
      const res = await accountingApi.resolveOrphans(companyId, orphans.map((o) => o.id));
      toast.success(`${res.reversed} verwaiste Buchung(en) storniert.`);
      loadArtifacts();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : 'Bereinigung fehlgeschlagen.'); }
    finally { setOrphanBusy(false); }
  }

  async function resolveArtifacts() {
    const booked = artifacts.filter((a) => a.booked).length;
    const ok = await confirm({
      title: 'Abschlussbuchungen bereinigen?',
      message: `${artifacts.length} Alt-System-Abschlussbuchung(en) (z. B. Auflösung GWG, EB-Wert) werden aus der Buchung genommen${booked ? `, davon ${booked} gebuchte per Storno (GoBD)` : ''}. Das sind keine echten Belege.`,
      confirmLabel: 'Bereinigen',
    });
    if (!ok) return;
    setResolving(true);
    try {
      const res = await accountingApi.resolveClosing(companyId, artifacts.map((a) => a.id));
      toast.success(`${res.cleared} bereinigt${res.reversed ? ` · ${res.reversed} storniert` : ''}.`);
      loadArtifacts();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : 'Bereinigung fehlgeschlagen.'); }
    finally { setResolving(false); }
  }

  async function loadSuggestion() {
    setLoading(true);
    try {
      const d = await accountingApi.accountMappingSuggestion(companyId);
      setRows(d.suggestions);
      setLoaded(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) setLocked(true);
      else toast.error(e instanceof ApiError ? e.message : 'Vorschlag fehlgeschlagen.');
    } finally { setLoading(false); }
  }

  function setAction(i: number, action: AccountMapping['action']) {
    setRows((rs) => rs ? rs.map((r, idx) => idx === i ? { ...r, action } : r) : rs);
  }

  async function apply() {
    if (!rows) return;
    const active = rows.filter((r) => r.action === 'create' || r.action === 'map');
    const ok = await confirm({
      title: 'Mapping anwenden?',
      message: `${active.length} Konten-Zuordnung(en) werden angewendet: fehlende Konten angelegt, Belege umgeleitet und verbucht. Als „Abschlussbuchung“ markierte Zeilen (z. B. Auflösung GWG, EB-Wert) werden – falls fälschlich gebucht – storniert und aus der Buchung genommen. Ignorierte Zeilen bleiben unangetastet.`,
      confirmLabel: 'Anwenden & buchen',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await accountingApi.applyAccountMapping(companyId, rows);
      const parts = [`${res.createdAccounts} Konten angelegt`, `${res.booked} Belege verbucht`];
      if (res.closingReversed) parts.push(`${res.closingReversed} Fehlbuchung(en) storniert`);
      toast.success(parts.join(' · ') + '.');
      loadSuggestion();
    } catch (e) { toast.error(e instanceof ApiError ? e.message : 'Anwenden fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  if (locked) {
    return <EmptyState title="Import-Hilfe ab Professional" hint="KI-Zuordnung fehlender Konten aus dem Alt-Import." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />;
  }

  const applicable = rows?.filter((r) => r.action === 'create' || r.action === 'map').length ?? 0;

  return (
    <div className="space-y-6">
      {orphans.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-red-700 dark:text-red-400">⚠ Verwaiste Buchungen — Doppelzählung!</h2>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Diese Buchungen gehören zu keinem Beleg mehr (z. B. doppelt erfasste Einnahmen aus dem Import). Sie verfälschen SuSa und EÜR. „Stornieren“ bucht sie GoBD-konform aus.</p>
          <Card className="!p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                    <th className="px-4 py-2">Datum</th><th className="px-2 py-2">Buchung</th><th className="px-4 py-2 text-right">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((o) => (
                    <tr key={o.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-4 py-1.5 text-gray-500">{new Date(o.date).toLocaleDateString('de-DE')}</td>
                      <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">{o.description ?? '—'}</td>
                      <td className="px-4 py-1.5 text-right text-gray-700 dark:text-gray-300">{euro(o.debitCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
              <span className="text-xs text-gray-400">{orphans.length} verwaiste Buchung(en) · {euro(orphans.reduce((s, o) => s + o.debitCents, 0))}</span>
              <Button onClick={resolveOrphans} busy={orphanBusy} disabled={orphanBusy}>Stornieren</Button>
            </div>
          </Card>
        </div>
      )}

      {artifacts.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Abschlussbuchungen aus dem Alt-System</h2>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Diese „Belege“ sind keine echten Geschäftsvorfälle, sondern Abschlussbuchungen (Auflösung GWG, EB-Wert, Saldovortrag). Sie dürfen nicht gegen Bank gebucht werden — bereinigen entfernt sie aus der Buchung (gebuchte werden GoBD-konform storniert).</p>
          <Card className="!p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                    <th className="px-4 py-2">Datum</th><th className="px-2 py-2">Bezeichnung</th><th className="px-2 py-2">Konto</th><th className="px-2 py-2 text-right">Betrag</th><th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {artifacts.map((a) => (
                    <tr key={a.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="px-4 py-1.5 text-gray-500">{new Date(a.date).toLocaleDateString('de-DE')}</td>
                      <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">{a.vendor ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-500">{a.accountNumber ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300">{euro(a.grossCents)}</td>
                      <td className="px-4 py-1.5">{a.booked ? <span className="text-amber-600 dark:text-amber-400">gebucht → wird storniert</span> : <span className="text-gray-400">offen</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
              <span className="text-xs text-gray-400">{artifacts.length} Abschlussbuchung(en)</span>
              <Button onClick={resolveArtifacts} busy={resolving} disabled={resolving}>Bereinigen</Button>
            </div>
          </Card>
        </div>
      )}

      <div>
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Import prüfen — fehlende Konten</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">Belege aus dem Alt-Import verweisen teils auf Konten, die im Kontenrahmen fehlen, und können daher nicht verbucht werden. Die KI schlägt je Konto eine fachlich korrekte Zuordnung vor — du gibst frei.</p>
      </div>

      {!loaded ? (
        <Card className="flex items-center justify-between gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-300">Vorschlag mit KI erstellen (liest nur, bucht noch nichts).</span>
          <Button onClick={loadSuggestion} busy={loading} disabled={loading}>✨ Vorschlag erstellen</Button>
        </Card>
      ) : !rows || rows.length === 0 ? (
        <EmptyState title="Keine fehlenden Konten" hint="Alle Belege verweisen auf vorhandene Konten." />
      ) : (
        <Card className="!p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                  <th className="px-4 py-2">Nr.</th>
                  <th className="px-2 py-2">Belege</th>
                  <th className="px-2 py-2">Vorschlag</th>
                  <th className="px-2 py-2">Ziel / Bezeichnung</th>
                  <th className="px-4 py-2">Begründung</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.number} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-4 py-1.5 font-mono text-gray-700 dark:text-gray-300">{r.number}</td>
                    <td className="px-2 py-1.5 text-gray-500">{r.count ?? '—'}</td>
                    <td className="px-2 py-1.5">
                      <select value={r.action} onChange={(e) => setAction(i, e.target.value as AccountMapping['action'])}
                        className={`rounded-md border border-gray-300 bg-transparent px-2 py-1 text-xs font-medium outline-none dark:border-gray-600 ${ACTION_COLOR[r.action]}`}>
                        {(['create', 'map', 'closing', 'skip'] as const).map((a) => <option key={a} value={a}>{ACTION_LABEL[a]}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                      {r.action === 'create' ? `${r.number} · ${r.name ?? ''}` : r.action === 'map' ? `→ ${r.targetNumber ?? '?'}` : '—'}
                    </td>
                    <td className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
            <span className="text-xs text-gray-400">{applicable} Konto/Konten werden angelegt bzw. umgeleitet und gebucht. „Abschlussbuchung“ &amp; „Ignorieren“ bleiben unangetastet.</span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={loadSuggestion} busy={loading} disabled={loading || busy}>Neu vorschlagen</Button>
              <Button onClick={apply} busy={busy} disabled={busy || applicable === 0}>Anwenden &amp; buchen</Button>
            </div>
          </div>
        </Card>
      )}
      </div>
    </div>
  );
}

// ---- Summen- und Saldenliste (DATEV) ----
// Saldo: + = Soll (S), - = Haben (H). Kontrolle: Summe aller Salden muss 0 sein.
function saldoLabel(cents: number): string {
  if (cents === 0) return '0,00 €';
  return `${euro(Math.abs(cents))} ${cents > 0 ? 'S' : 'H'}`;
}
function betrag(cents: number): string {
  return cents === 0 ? '–' : euro(cents);
}

function SusaTab({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<Susa | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    accountingApi.susa(companyId, year).then((d) => setData(d.susa)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [companyId, year]);

  async function exportPdf() {
    setExporting(true);
    try {
      const blob = await accountingApi.susaPdf(companyId, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `SuSa-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } finally { setExporting(false); }
  }

  const years: number[] = [];
  for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 5; y--) years.push(y);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Summen- und Saldenliste</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">DATEV-Aufbau · je Konto EB-Wert, Soll, Haben und Saldo. EB-Werte erst nach Jahresabschluss des Vorjahres (Bestandskonten).</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="secondary" onClick={exportPdf} busy={exporting} disabled={exporting || !data || data.rows.length === 0}>PDF-Export</Button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : !data || data.rows.length === 0 ? (
        <EmptyState title="Keine Buchungen im Jahr" hint="Für dieses Jahr sind noch keine Buchungen erfasst." />
      ) : (
        <Card className="!p-0">
          {!data.balanced && (
            <div className="rounded-t-2xl bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
              ⚠ Saldenkontrolle nicht bestanden — Differenz {saldoLabel(data.sumSaldoCents)}. Buchungen prüfen.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
                  <th className="px-4 py-2">Konto</th>
                  <th className="px-2 py-2">Bezeichnung</th>
                  <th className="px-2 py-2 text-right">EB-Wert</th>
                  <th className="px-2 py-2 text-right">Soll</th>
                  <th className="px-2 py-2 text-right">Haben</th>
                  <th className="px-4 py-2 text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.number} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="px-4 py-1.5 font-mono text-gray-700 dark:text-gray-300">{r.number}</td>
                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{r.name}</td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{r.ebCents === 0 ? '–' : saldoLabel(r.ebCents)}</td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{betrag(r.debitCents)}</td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{betrag(r.creditCents)}</td>
                    <td className="px-4 py-1.5 text-right font-medium text-gray-900 dark:text-gray-100">{saldoLabel(r.saldoCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 font-semibold dark:border-gray-700">
                  <td className="px-4 py-2" colSpan={3}>Summen</td>
                  <td className="px-2 py-2 text-right">{betrag(data.sumDebitCents)}</td>
                  <td className="px-2 py-2 text-right">{betrag(data.sumCreditCents)}</td>
                  <td className="px-4 py-2 text-right">{saldoLabel(data.sumSaldoCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {data.balanced && (
            <div className="rounded-b-2xl border-t border-gray-100 px-4 py-2 text-xs text-emerald-700 dark:border-gray-800 dark:text-emerald-400">
              ✓ Saldenkontrolle bestanden: Soll = Haben, Summe der Salden = 0,00 €.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ---- Jahresabschluss ----
function ClosingTab({ companyId, confirm, onNavigate }: { companyId: string; confirm: ReturnType<typeof useConfirm>; onNavigate: (tab: Tab) => void }): ReactElement {
  const toast = useToast();
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState<YearClosingChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);

  function load() {
    setLoading(true);
    accountingApi.yearClosing(companyId, year)
      .then((d) => { setData(d); setLocked(false); })
      .catch((e) => { if (e instanceof ApiError && e.status === 402) setLocked(true); else setData(null); })
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, year]);

  async function close() {
    const ok = await confirm({
      title: `Jahr ${year} abschließen?`,
      message: 'Danach sind im Jahr keine Buchungen, Belege oder Stornos mehr möglich (GoBD-Festschreibung). Du kannst den Abschluss bei Bedarf wieder aufheben.',
      confirmLabel: 'Jahr abschließen',
    });
    if (!ok) return;
    setBusy(true);
    try { await accountingApi.closeYear(companyId, year); toast.success(`Jahr ${year} abgeschlossen.`); load(); }
    catch (e) { toast.error(e instanceof ApiError ? e.message : 'Abschluss fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  async function reopen() {
    const ok = await confirm({
      title: `Abschluss ${year} aufheben?`,
      message: 'Das Jahr wird wieder bearbeitbar. Buchungen sind dann wieder möglich.',
      confirmLabel: 'Aufheben',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await accountingApi.reopenYear(companyId, year); toast.info(`Abschluss ${year} aufgehoben.`); load(); }
    catch (e) { toast.error(e instanceof ApiError ? e.message : 'Aufheben fehlgeschlagen.'); }
    finally { setBusy(false); }
  }

  const years: number[] = [];
  for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 5; y--) years.push(y);

  const sevIcon: Record<string, string> = { error: '✗', warn: '⚠', info: 'ℹ' };

  /**
   * Ein beanstandeter Datensatz als Sprungziel. Belege und Rechnungen liegen
   * auf eigenen Seiten (tiefer Link), Anlagen und verwaiste Buchungen auf
   * dieser Seite — dort wird nur der Tab gewechselt.
   */
  function ChecklistRefLink({ refItem }: { refItem: ChecklistRef }): ReactElement {
    const inhalt = (
      <>
        <span className="truncate">{refItem.label}</span>
        {refItem.hint && <span className="shrink-0 text-gray-400">· {refItem.hint}</span>}
        {refItem.amountCents != null && (
          <span className="ml-auto shrink-0 tabular-nums text-gray-500 dark:text-gray-400">{euro(refItem.amountCents)}</span>
        )}
      </>
    );
    const css =
      'flex w-full items-baseline gap-1.5 rounded-md px-2 py-1 text-left text-xs text-blue-700 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-gray-700/50';

    if (refItem.target === 'receipt') {
      return <Link to={`/app/belege?receipt=${encodeURIComponent(refItem.id)}`} className={css}>{inhalt}</Link>;
    }
    if (refItem.target === 'invoice') {
      return <Link to={`/app/rechnungen/${encodeURIComponent(refItem.id)}`} className={css}>{inhalt}</Link>;
    }
    // Anlagen/Buchungen: gleicher Screen, nur anderer Tab.
    const ziel: Tab = refItem.target === 'asset' ? 'assets' : 'import';
    return <button type="button" onClick={() => onNavigate(ziel)} className={css}>{inhalt}</button>;
  }

  const sevColor = (ok: boolean, sev: string) =>
    ok ? 'text-emerald-600 dark:text-emerald-400'
      : sev === 'error' ? 'text-red-600 dark:text-red-400'
      : sev === 'warn' ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-500';

  if (locked) {
    return <EmptyState title="Jahresabschluss ab Professional" hint="Prüf-Checkliste, Festschreibung & Saldovortrag." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Jahresabschluss (EÜR)</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Prüfen, abschließen und festschreiben. Danach trägt die SuSa des Folgejahres die EB-Werte vor.</p>
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : !data ? (
        <EmptyState title="Keine Daten" hint="Für dieses Jahr konnten keine Daten geladen werden." />
      ) : (
        <div className="space-y-4">
          {data.alreadyClosed && (
            <div className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
              ✓ Jahr {year} ist abgeschlossen{data.closedAt ? ` am ${new Date(data.closedAt).toLocaleDateString('de-DE')}` : ''}. Buchungen in diesem Jahr sind gesperrt.
            </div>
          )}

          <Card>
            <div className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">Prüf-Checkliste</div>
            <ul className="space-y-2">
              {data.checks.map((c) => (
                <li key={c.key} className="flex items-start gap-2 text-sm">
                  <span className={`mt-0.5 font-bold ${sevColor(c.ok, c.severity)}`}>{c.ok ? '✓' : sevIcon[c.severity]}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-gray-800 dark:text-gray-200">{c.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{c.detail}</span>
                    {/* Die beanstandeten Datensaetze direkt anspringbar machen —
                        sonst muesste der Nutzer sie von Hand suchen. */}
                    {!c.ok && c.refs && c.refs.length > 0 && (
                      <span className="mt-1.5 block space-y-1">
                        {c.refs.map((r) => (
                          <ChecklistRefLink key={`${r.target}-${r.id}`} refItem={r} />
                        ))}
                        {c.refsTruncated && (
                          <span className="block pt-0.5 text-xs text-gray-400">
                            … weitere werden nicht einzeln aufgelistet.
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <div className="text-gray-500 dark:text-gray-400">Gewinn (EÜR) {year}</div>
              <div className={`text-lg font-bold ${data.gewinnCents >= 0 ? 'text-gray-900 dark:text-gray-100' : 'text-red-600'}`}>{euro(data.gewinnCents)}</div>
              <div className="text-xs text-gray-400">Einnahmen {euro(data.einnahmenCents)} · Ausgaben {euro(data.ausgabenCents)}</div>
            </div>
            {data.alreadyClosed ? (
              <Button variant="secondary" onClick={reopen} busy={busy} disabled={busy}>Abschluss aufheben</Button>
            ) : (
              <Button onClick={close} busy={busy} disabled={busy || !data.canClose}>Jahr abschließen</Button>
            )}
          </Card>

          {!data.alreadyClosed && !data.canClose && (
            <p className="text-xs text-red-500">Abschluss nicht möglich: Es gibt offene Pflichtpunkte (rot). Bitte zuerst beheben.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Buchungen (Journal) ----
function JournalTab({ companyId, confirm }: { companyId: string; confirm: ReturnType<typeof useConfirm> }): ReactElement {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [booking, setBooking] = useState(false);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState<'year' | 'month'>('year');
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1); // 1..12
  const toast = useToast();

  async function bookReceipts() {
    setBooking(true);
    try {
      const r = await receiptApi.bookAll(companyId);
      if (r.booked > 0) { toast.success(`${r.booked} Beleg(e) verbucht.`); load(); }
      else if (r.candidates === 0) toast.info('Alle Belege sind bereits verbucht.');
      else toast.info('Keine buchbaren Belege gefunden (Konto fehlt?).');
    } catch (e) { setError(e); } finally { setBooking(false); }
  }

  // Buchungs-Eingabe: 2 Zeilen (Soll-Konto, Haben-Konto, Betrag).
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState('');
  const [debitAcc, setDebitAcc] = useState('');
  const [creditAcc, setCreditAcc] = useState('');
  const [amount, setAmount] = useState('');

  const amountCents = Math.round((parseFloat(amount.replace(',', '.')) || 0) * 100);
  const accLabel = (id: string) => { const a = accounts.find((x) => x.id === id); return a ? `${a.number} · ${a.name}` : '—'; };
  const journalReady = !!debitAcc && !!creditAcc && amountCents > 0 && debitAcc !== creditAcc;

  function load() {
    const filter = viewMode === 'month' ? { year: viewYear, month: viewMonth } : { year: viewYear };
    accountingApi.journal(companyId, filter).then(setEntries).catch(() => setEntries([]));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, viewYear, viewMode, viewMonth]);
  useEffect(() => {
    accountingApi.accounts(companyId).then(setAccounts).catch(() => undefined);
  }, [companyId]);

  async function save() {
    setError(null);
    if (!journalReady) {
      setTouched(true);
      if (debitAcc && debitAcc === creditAcc) setError(new Error('Soll- und Haben-Konto müssen unterschiedlich sein.'));
      return;
    }
    const cents = amountCents;
    setBusy(true);
    try {
      await accountingApi.addEntry(companyId, {
        date,
        type: 'general',
        description: desc || undefined,
        lines: [
          { accountId: debitAcc, debitCents: cents, creditCents: 0 },
          { accountId: creditAcc, debitCents: 0, creditCents: cents },
        ],
      });
      setOpen(false);
      setAmount(''); setDesc('');
      load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function reverse(id: string) {
    const ok = await confirm({ title: 'Buchung stornieren?', message: 'Es wird eine Gegenbuchung erstellt (GoBD-konform).', confirmLabel: 'Stornieren' });
    if (!ok) return;
    await accountingApi.reverse(companyId, id);
    load();
  }

  const journalYears: number[] = [];
  for (let y = new Date().getFullYear(); y >= new Date().getFullYear() - 5; y--) journalYears.push(y);
  function stepMonth(dir: -1 | 1) {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m < 1) { m = 12; y -= 1; }
    else if (m > 12) { m = 1; y += 1; }
    setViewMonth(m); setViewYear(y);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Jahr / Monat Umschalter */}
          <div className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
            {(['year', 'month'] as const).map((m) => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition ${viewMode === m ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-300'}`}>
                {m === 'year' ? 'Jahr' : 'Monat'}
              </button>
            ))}
          </div>
          <select value={viewYear} onChange={(e) => setViewYear(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800">
            {journalYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {viewMode === 'month' && (
            <div className="inline-flex items-center gap-1">
              <Button variant="ghost" onClick={() => stepMonth(-1)} aria-label="Vorheriger Monat">‹</Button>
              <span className="min-w-[2.5rem] text-center text-sm font-medium text-gray-700 dark:text-gray-200">{MONTH_NAMES[viewMonth - 1]}</span>
              <Button variant="ghost" onClick={() => stepMonth(1)} aria-label="Nächster Monat">›</Button>
            </div>
          )}
          <span className="text-xs text-gray-400">{entries.length} Buchung{entries.length === 1 ? '' : 'en'}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={bookReceipts} busy={booking} disabled={booking}>Belege verbuchen</Button>
          <Button onClick={() => { setTouched(false); setError(null); setOpen(true); }}>+ Neue Buchung</Button>
        </div>
      </div>
      {entries.length === 0 ? (
        <EmptyState title="Keine Buchungen" hint={viewMode === 'month' ? `Im ${MONTH_NAMES[viewMonth - 1]} ${viewYear} gibt es keine Buchungen.` : `Im Jahr ${viewYear} gibt es keine Buchungen.`} />
      ) : (
        <div className="space-y-2">
          {entries.map((e) => {
            const sum = e.lines.reduce((s, l) => s + l.debitCents, 0);
            return (
              <Card key={e.id} className="!p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{e.description || 'Buchung'}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(e.date).toLocaleDateString('de-DE')}
                      {e.lines.map((l) => ` · ${l.account?.number ?? ''}${l.debitCents ? ' (S)' : ' (H)'}`).join('')}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{euro(sum)}</span>
                    <Button variant="ghost" onClick={() => reverse(e.id)}>Stornieren</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">Neue Buchung</h2>
            <p className="mb-4 mt-1 text-sm text-gray-500">Jede Buchung hat eine Soll- und eine Haben-Seite über denselben Betrag.</p>
            <div className="space-y-4">
              {error ? <ErrorNote error={error} /> : null}
              <Field label="Datum" type="date" value={date} onChange={setDate} required />
              <Field label="Beschreibung" value={desc} onChange={setDesc} placeholder="z. B. Büromaterial bar bezahlt" hint="Was wurde gebucht? Erscheint im Journal." />
              <AccountSelect label="Soll-Konto" accounts={accounts} value={debitAcc} onChange={setDebitAcc} required error={touched && !debitAcc} hint="Wohin der Wert fließt – z. B. Aufwand oder Anlage." />
              <AccountSelect label="Haben-Konto" accounts={accounts} value={creditAcc} onChange={setCreditAcc} required error={touched && !creditAcc} hint="Woher der Wert kommt – z. B. Bank, Kasse oder Erlös." />
              <Field label="Betrag €" value={amount} onChange={setAmount} placeholder="0,00" inputMode="decimal" required error={touched && amountCents <= 0} />

              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Buchungssatz</div>
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Soll</span> {accLabel(debitAcc)} <span className="text-gray-400">an</span> <span className="font-medium">Haben</span> {accLabel(creditAcc)}
                </div>
                <div className="mt-1 text-sm text-gray-500">Betrag: {amountCents > 0 ? euro(amountCents) : '—'}</div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{journalReady ? 'Buchung ist ausgeglichen' : 'Soll, Haben und Betrag nötig'}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button onClick={save} busy={busy}>Buchen</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountSelect({ label, accounts, value, onChange, hint, required, error }: { label: string; accounts: Account[]; value: string; onChange: (v: string) => void; hint?: string; required?: boolean; error?: boolean }): ReactElement {
  const cls =
    'w-full rounded-xl border px-4 py-2.5 text-base outline-none focus:ring-2 ' +
    (error ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200');
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700">
        {label}{required && <span className="text-red-500"> *</span>}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">— wählen —</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>{a.number} · {a.name}</option>
        ))}
      </select>
      {error ? <span className="mt-1 block text-xs text-red-500">Bitte wählen.</span> : hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
    </label>
  );
}

// ---- Anlagen (AfA) ----
function AssetsTab({ companyId, confirm }: { companyId: string; confirm: ReturnType<typeof useConfirm> }): ReactElement {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [cost, setCost] = useState('');
  const [years, setYears] = useState('3');
  const [method, setMethod] = useState<'linear' | 'gwg'>('linear');
  const [methodTouched, setMethodTouched] = useState(false); // hat der Nutzer die Methode selbst gewaehlt?
  const [receiptId, setReceiptId] = useState('');
  const [linkable, setLinkable] = useState<{ id: string; date: string; vendor: string | null; description: string | null; grossCents: number; netCents: number | null }[]>([]);
  const [touched, setTouched] = useState(false);

  const costCents = Math.round((parseFloat(cost.replace(',', '.')) || 0) * 100);
  const yearsNum = parseInt(years) || 0;
  const isGwgEligible = costCents > 0 && costCents <= 80000; // GWG-Grenze 800 € netto
  const isGwg = method === 'gwg';
  const annualAfa = isGwg ? costCents : (yearsNum > 0 ? Math.round(costCents / yearsNum) : 0);
  const assetReady = !!name.trim() && costCents > 0 && (isGwg || yearsNum > 0);

  // Auto-GWG: solange der Nutzer die Methode nicht selbst gesetzt hat, aus den
  // Kosten ableiten (≤ 800 € netto -> GWG-Sofortabschreibung, sonst linear).
  useEffect(() => {
    if (methodTouched || costCents === 0) return;
    setMethod(isGwgEligible ? 'gwg' : 'linear');
  }, [costCents, isGwgEligible, methodTouched]);

  function load() {
    accountingApi.assets(companyId).then(setAssets).catch((e) => {
      if (e?.status === 402) setLocked(true);
    });
  }
  useEffect(() => {
    load();
    accountingApi.linkableReceipts(companyId).then(setLinkable).catch(() => setLinkable([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (locked) {
    return <EmptyState title="Anlagen & Abschreibungen ab Professional" hint="Upgrade deinen Tarif, um AfA zu nutzen." action={<a href="/app/einstellungen/tarif"><Button>Tarif ansehen</Button></a>} />;
  }

  async function save() {
    setError(null);
    if (!assetReady) {
      setTouched(true);
      return;
    }
    setBusy(true);
    try {
      await accountingApi.addAsset(companyId, {
        name: name.trim(), purchaseDate: date, costCents,
        usefulLifeYears: isGwg ? 1 : yearsNum,
        method,
        ...(receiptId ? { receiptId } : {}),
      });
      setOpen(false); setName(''); setCost(''); setReceiptId(''); setMethodTouched(false);
      load();
      accountingApi.linkableReceipts(companyId).then(setLinkable).catch(() => undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Bei Beleg-Auswahl: Bezeichnung/Datum/Kosten vorbefuellen (sofern leer).
  function pickReceipt(id: string) {
    setReceiptId(id);
    const r = linkable.find((x) => x.id === id);
    if (!r) return;
    if (!name.trim()) setName(r.vendor || r.description || 'Anlagegut');
    setDate(new Date(r.date).toISOString().slice(0, 10));
    const net = r.netCents ?? r.grossCents;
    if (!cost.trim()) setCost((net / 100).toFixed(2));
  }

  async function remove(id: string) {
    const ok = await confirm({ title: 'Anlage löschen?', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    await accountingApi.removeAsset(companyId, id);
    load();
  }

  return (
    <div>
      <AfaRunPanel companyId={companyId} confirm={confirm} onPosted={load} />
      <div className="mb-4 flex justify-end">
        <Button onClick={() => { setTouched(false); setError(null); setOpen(true); }}>+ Neue Anlage</Button>
      </div>
      {assets.length === 0 ? (
        <EmptyState title="Noch keine Anlagegüter" hint="Erfasse Anlagen für die automatische Abschreibung (AfA)." />
      ) : (
        <div className="space-y-2">
          {assets.map((a) => (
            <AssetCard key={a.id} companyId={companyId} asset={a} onChanged={load} onRemove={() => remove(a.id)} />
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 dark:bg-gray-800">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Neue Anlage</h2>
            <p className="mb-4 mt-1 text-sm text-gray-500 dark:text-gray-400">Anlagegüter werden abgeschrieben (AfA). Bis 800 € netto als GWG sofort voll absetzbar.</p>
            <div className="space-y-4">
              {error ? <ErrorNote error={error} /> : null}

              {linkable.length > 0 && (
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Beleg (optional)</span>
                  <select value={receiptId} onChange={(e) => pickReceipt(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800">
                    <option value="">— kein Beleg —</option>
                    {linkable.map((r) => (
                      <option key={r.id} value={r.id}>{new Date(r.date).toLocaleDateString('de-DE')} · {(r.vendor || r.description || 'Beleg').slice(0, 40)} · {euro(r.grossCents)}</option>
                    ))}
                  </select>
                  <span className="mt-1 block text-xs text-gray-400">Verknüpft die Anlage mit einem vorhandenen Beleg und füllt die Felder vor.</span>
                </label>
              )}

              <Field label="Bezeichnung" value={name} onChange={setName} placeholder="z. B. Laptop, Bürostuhl, Maschine" required error={touched && !name.trim()} />
              <Field label="Anschaffungsdatum" type="date" value={date} onChange={setDate} required />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Anschaffungskosten €" value={cost} onChange={setCost} placeholder="0,00" inputMode="decimal" required error={touched && costCents <= 0} hint="Netto, ohne Vorsteuer." />
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Abschreibung</span>
                  <select value={method} onChange={(e) => { setMethod(e.target.value as 'linear' | 'gwg'); setMethodTouched(true); }}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800">
                    <option value="gwg">GWG – sofort (100 %)</option>
                    <option value="linear">Linear über Nutzungsdauer</option>
                  </select>
                  <span className="mt-1 block text-xs text-gray-400">{isGwgEligible ? 'Bis 800 € netto: GWG empfohlen.' : 'Über 800 € netto: linear.'}</span>
                </label>
              </div>
              {!isGwg && (
                <Field label="Nutzungsdauer (Jahre)" value={years} onChange={setYears} inputMode="numeric" required error={touched && yearsNum <= 0} hint="Laut AfA-Tabelle, z. B. PC 3 J." />
              )}

              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Vorschau Abschreibung</div>
                {assetReady ? (
                  isGwg ? (
                    <div className="space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                      <div>Sofort voll abgeschrieben: <span className="font-medium">{euro(costCents)}</span> im Jahr {new Date(date).getFullYear()}</div>
                      <div className="text-gray-500">Restbuchwert: {euro(0)} (GWG, §6 Abs. 2 EStG)</div>
                    </div>
                  ) : (
                    <div className="space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                      <div>AfA pro Jahr: <span className="font-medium">{euro(annualAfa)}</span> ({yearsNum} Jahre linear)</div>
                      <div className="text-gray-500">Restwert nach Jahr 1: {euro(costCents - annualAfa)}</div>
                    </div>
                  )
                ) : (
                  <div className="text-sm text-gray-400">Kosten {isGwg ? '' : 'und Nutzungsdauer '}eingeben…</div>
                )}
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">{assetReady ? 'Bereit zum Speichern' : 'Pflichtfelder ausfüllen'}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
                <Button onClick={save} busy={busy}>Speichern</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AfaRunPanel({ companyId, confirm, onPosted }: { companyId: string; confirm: ReturnType<typeof useConfirm>; onPosted: () => void }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState<AfaRunPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function loadPreview(y: number) {
    accountingApi.afaRunPreview(companyId, y).then(setPreview).catch(() => setPreview(null));
  }
  useEffect(() => { loadPreview(year); setMsg(null); /* eslint-disable-next-line */ }, [year, companyId]);

  async function run() {
    if (!preview || preview.openCount === 0) return;
    const ok = await confirm({
      title: `AfA ${year} buchen?`,
      message: `${preview.openCount} Anlage(n), ${euro(preview.openCents)} werden als Abschreibung ins Journal gebucht (Soll Abschreibungen / Haben Anlagekonto).`,
      confirmLabel: 'AfA buchen',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await accountingApi.afaRun(companyId, year);
      setMsg(`${res.posted} Buchung(en) erstellt · ${euro(res.totalCents)} abgeschrieben.`);
      loadPreview(year);
      onPosted();
    } finally { setBusy(false); }
  }

  const postedCount = preview ? preview.rows.filter((r) => r.alreadyPosted).length : 0;

  async function reverse() {
    const ok = await confirm({
      title: `AfA ${year} stornieren?`,
      message: `Die gebuchte Abschreibung für ${year} (${postedCount} Anlage(n)) wird per Gegenbuchung storniert (GoBD-konform). Danach kann ${year} neu gebucht werden.`,
      confirmLabel: 'Stornieren',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await accountingApi.afaReverse(companyId, year);
      setMsg(`${res.reversed} Buchung(en) storniert · ${euro(res.totalCents)} rückgängig.`);
      loadPreview(year);
      onPosted();
    } finally { setBusy(false); }
  }

  return (
    <Card className="mb-4 !p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">AfA-Jahreslauf</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Bucht die lineare Jahres-Abschreibung aller Anlagen ins Journal. Fließt automatisch in EÜR/BWA.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
          {postedCount > 0 && (
            <Button variant="ghost" onClick={reverse} busy={busy}>Storno</Button>
          )}
          <Button onClick={run} busy={busy} disabled={!preview || preview.openCount === 0}>
            {preview && preview.openCount > 0 ? `AfA buchen (${euro(preview.openCents)})` : 'Nichts offen'}
          </Button>
        </div>
      </div>
      {msg && <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">{msg}</div>}
      {preview && preview.rows.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-xs dark:border-gray-700">
          {preview.rows.map((r) => (
            <div key={r.assetId} className="flex justify-between text-gray-600 dark:text-gray-300">
              <span>{r.name}{r.accountNumber ? ` · Konto ${r.accountNumber}` : ''}</span>
              <span className={r.alreadyPosted ? 'text-gray-400' : 'font-medium'}>
                {euro(r.amountCents)}{r.alreadyPosted ? ' · bereits gebucht' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AssetCard({ companyId, asset, onChanged, onRemove }: { companyId: string; asset: Asset; onChanged: () => void; onRemove: () => void }): ReactElement {
  const [editing, setEditing] = useState(false);
  const [years, setYears] = useState(String(asset.usefulLifeYears));
  const [busy, setBusy] = useState(false);
  // "Ausbuchen" = Abgang erfassen (verkauft, entsorgt, privat entnommen).
  const [disposing, setDisposing] = useState(false);
  const [dispDate, setDispDate] = useState(new Date().toISOString().slice(0, 10));
  const [proceeds, setProceeds] = useState('');
  const [dispError, setDispError] = useState<string | null>(null);
  const disposed = Boolean(asset.disposalDate);

  async function save() {
    const y = parseInt(years);
    if (!y || y <= 0) return;
    setBusy(true);
    try { await accountingApi.updateAsset(companyId, asset.id, { usefulLifeYears: y }); setEditing(false); onChanged(); }
    finally { setBusy(false); }
  }

  async function saveDisposal() {
    setBusy(true);
    setDispError(null);
    try {
      await accountingApi.updateAsset(companyId, asset.id, {
        disposalDate: dispDate,
        disposalProceedsCents: Math.round(parseFloat(proceeds.replace(',', '.') || '0') * 100),
      });
      setDisposing(false);
      onChanged();
    } catch (e) {
      setDispError(e instanceof Error ? e.message : 'Konnte nicht gespeichert werden.');
    } finally { setBusy(false); }
  }

  async function undoDisposal() {
    setBusy(true);
    try { await accountingApi.updateAsset(companyId, asset.id, { disposalDate: null, disposalProceedsCents: 0 }); onChanged(); }
    finally { setBusy(false); }
  }

  return (
    <Card className="!p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">{asset.name}</span>
            {asset.source === 'ocr' && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">automatisch aus Beleg</span>
            )}
            {disposed && (
              <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                abgegeben am {new Date(asset.disposalDate!).toLocaleDateString('de-DE')}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {euro(asset.costCents)} · {asset.usefulLifeYears} Jahre Nutzungsdauer
            {asset.accountNumber ? ` · Konto ${asset.accountNumber}` : ''} · seit {new Date(asset.purchaseDate).toLocaleDateString('de-DE')}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!disposed && (
            <Button variant="ghost" onClick={() => { setYears(String(asset.usefulLifeYears)); setEditing((v) => !v); }}>Nutzungsdauer</Button>
          )}
          {disposed ? (
            <Button variant="ghost" onClick={undoDisposal} busy={busy}>Rückgängig</Button>
          ) : (
            <Button variant="ghost" onClick={() => { setDispError(null); setDisposing((v) => !v); }}>Nicht mehr da</Button>
          )}
          <Button variant="ghost" onClick={onRemove}>Löschen</Button>
        </div>
      </div>

      {disposing && (
        <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
            Verkauft, entsorgt oder privat übernommen? Dann wird ab diesem Datum nicht weiter abgeschrieben.
          </p>
          {dispError ? <div className="mb-2"><ErrorNote error={dispError} /></div> : null}
          <div className="flex flex-wrap items-end gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Wann?</span>
              <input type="date" value={dispDate} onChange={(e) => setDispDate(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Verkaufspreis €</span>
              <input value={proceeds} onChange={(e) => setProceeds(e.target.value)} placeholder="0,00" inputMode="decimal"
                className="w-28 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800" />
            </label>
            <Button onClick={saveDisposal} busy={busy}>Speichern</Button>
            <button onClick={() => setDisposing(false)} className="text-xs text-gray-500 hover:underline">abbrechen</button>
          </div>
          <p className="mt-2 text-xs text-gray-400">Bei Entsorgung oder Verschrottung einfach 0,00 lassen.</p>
        </div>
      )}
      {editing && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-900">
          <span className="text-sm text-gray-600 dark:text-gray-300">Nutzungsdauer (Jahre):</span>
          <input type="number" value={years} min={1} max={50} onChange={(e) => setYears(e.target.value)}
            className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
          <Button onClick={save} busy={busy}>Speichern</Button>
          <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">abbrechen</button>
        </div>
      )}
      {asset.depreciation && asset.depreciation.length > 0 && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          AfA/Jahr: {euro(asset.depreciation[0]!.amountCents)} · Restwert Ende Jahr 1: {euro(asset.depreciation[0]!.bookValueCents)}
        </div>
      )}
    </Card>
  );
}
